// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatToolStepBody } from "./formatToolStepBody";
import {
  STATUS_DONE_ICON_CLASS,
  STATUS_PANEL_CARD_MUTED_CLASS,
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";
import { stringField } from "./truncatedJson";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

export type SubagentReportView = {
  name?: string;
  status?: string;
  summary?: string;
  error?: string;
  output?: string;
};

export type SubagentCardEntry = {
  /** Suffix for the card testids; matches the list-row ids it replaces. */
  rowId: string;
  title: string;
  body: string;
  report?: SubagentReportView;
  done: boolean;
  /**
   * warhol says this leg did not do the work. Wins over the report: while the
   * turn runs there is no report to read.
   */
  failed?: boolean;
};

export const SUBAGENT_CARD_GRID_CLASS =
  "grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-2";

export function reportDetail(report: SubagentReportView | undefined): string {
  if (!report) return "";
  for (const field of [report.summary, report.error, report.output]) {
    // `output` is the subagent's typed result as a JSON string; the formatter
    // turns it (and any structured summary) into labelled lines.
    const text = formatToolStepBody(stringField(field));
    if (text) return text;
  }
  return stringField(report.status);
}

/**
 * warhol's `SubagentReport.status` is `Literal["ok", "failed", "timed_out"]`
 * (`agents/_subagents.py`); only `ok` carries an output.
 */
const FAILED_SUBAGENT_STATUSES = new Set(["failed", "timed_out"]);

/**
 * Whether a report says its subagent did NOT do the work.
 *
 * The row used to tick as soon as the fan-out returned, so a reviewer that
 * failed or timed out was drawn exactly like one that finished -- the chip
 * claimed work that never happened. An unknown status is not read as a
 * failure: a vocabulary this does not know is a reason to stay quiet, not to
 * accuse.
 */
export function subagentReportFailed(
  report: SubagentReportView | undefined,
): boolean {
  if (!report) return false;
  return FAILED_SUBAGENT_STATUSES.has(stringField(report.status).toLowerCase());
}

/** Warhol agent slugs (`regulated-copy`, `codes_values`) as Title Case. */
export function agentDisplayName(name: string): string {
  return name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** A task or skill body, readable even when the caller passed JSON. */
export function readableBody(body: string): string {
  return formatToolStepBody(body);
}

// why: AgentSteps re-renders on every stream tick, and this walk JSON.parses.
export function useReadableSubagentText(
  rawBody: string,
  report: SubagentReportView | undefined,
): { body: string; reportText: string } {
  const body = useMemo(() => readableBody(rawBody), [rawBody]);
  const summary = report?.summary;
  const error = report?.error;
  const output = report?.output;
  const status = report?.status;
  const hasReport = report != null;
  const reportText = useMemo(
    () =>
      reportDetail(hasReport ? { summary, error, output, status } : undefined),
    [hasReport, summary, error, output, status],
  );
  return { body, reportText };
}

/**
 * Progress-rail grays: a filled check once settled, a spinner while running.
 * A failed or timed-out subagent keeps the red cross so it never reads as done.
 */
function StatusGlyph({
  done,
  failed = false,
  testIdSuffix,
}: {
  done: boolean;
  failed?: boolean;
  testIdSuffix: string;
}) {
  if (failed) {
    return (
      <XCircle
        size={14}
        weight="fill"
        data-testid={`agent-step-subagent-failed-${testIdSuffix}`}
        className="text-tomato-900 shrink-0"
        aria-hidden
      />
    );
  }
  return done ? (
    <CheckCircle
      size={14}
      weight="fill"
      data-testid={`agent-step-subagent-done-${testIdSuffix}`}
      className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
      aria-hidden
    />
  ) : (
    <CircleNotch
      size={14}
      data-testid={`agent-step-subagent-running-${testIdSuffix}`}
      className="shrink-0 animate-spin text-puntt-neutral-gray-9 motion-reduce:animate-none"
      aria-hidden
    />
  );
}

function SubagentCard({
  rowId,
  title,
  body: rawBody,
  report,
  done,
  failed: failedStatus,
}: SubagentCardEntry) {
  const [expanded, setExpanded] = useState(false);
  const { body, reportText } = useReadableSubagentText(rawBody, report);
  const hasDetail = !!body || !!reportText;
  const open = hasDetail && expanded;
  const failed = failedStatus ?? subagentReportFailed(report);

  const header = (
    <>
      <span className="flex items-center justify-between gap-2">
        <StatusGlyph done={done} failed={failed} testIdSuffix={rowId} />
        {hasDetail ? (
          open ? (
            <CaretDown
              size={14}
              className="shrink-0 text-puntt-neutral-gray-12"
              aria-hidden
            />
          ) : (
            <CaretRight
              size={14}
              className="shrink-0 text-puntt-neutral-gray-12"
              aria-hidden
            />
          )
        ) : null}
      </span>
      <Text
        size="1"
        weight="regular"
        title={title}
        data-testid="agent-step-name"
        className="min-w-0 truncate text-puntt-neutral-gray-12"
      >
        {title}
      </Text>
    </>
  );

  return (
    <div
      data-testid={`agent-step-subagent-${rowId}`}
      className={cx(
        "min-w-0 overflow-hidden",
        // why: `open: true` keeps the gray-3 stroke an expanded chat toggle
        // has; without a stroke a transparent card has no edge in the grid.
        interactiveSurfaceClasses({
          expandable: hasDetail,
          muted: true,
          compact: true,
          open: true,
        }),
        // why: spanning every column is what drops an open card onto its own
        // row at the grid's width and wraps the cards after it underneath.
        open && "col-span-full",
      )}
    >
      {hasDetail ? (
        <button
          type="button"
          data-testid={`agent-step-subagent-toggle-${rowId}`}
          aria-expanded={open}
          aria-label={title}
          onClick={() => setExpanded((value) => !value)}
          className={`${STATUS_PANEL_CARD_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
        >
          {header}
        </button>
      ) : (
        <div className={STATUS_PANEL_CARD_MUTED_CLASS}>{header}</div>
      )}
      {open ? (
        <div
          data-testid={`agent-step-subagent-body-${rowId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-12"
            >
              {body}
            </Text>
          ) : null}
          {reportText ? (
            <Text
              size="1"
              className={`whitespace-pre-wrap text-puntt-neutral-gray-9 ${body ? "mt-1" : ""}`}
            >
              {reportText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Sub-agents from one fan-out as peer cards; an opened card takes a full row. */
export function SubagentCardGrid({
  gridId,
  entries,
}: {
  gridId: string;
  entries: SubagentCardEntry[];
}) {
  return (
    <div
      data-testid={`agent-step-subagent-grid-${gridId}`}
      className={SUBAGENT_CARD_GRID_CLASS}
    >
      {entries.map((entry) => (
        <SubagentCard key={entry.rowId} {...entry} />
      ))}
    </div>
  );
}

/** Collapsed "N agents" toggle that nests the fan-out card grid. */
export function SubagentCardGroup({
  gridId,
  entries,
}: {
  gridId: string;
  entries: SubagentCardEntry[];
}) {
  const { t } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);
  const entryFailed = (entry: SubagentCardEntry) =>
    entry.failed ?? subagentReportFailed(entry.report);
  // A failed tick arrives with `done: false`; it has still settled.
  const allDone = entries.every((entry) => entry.done || entryFailed(entry));
  // Only every member failing fails the group; one bad reviewer among good
  // ones belongs on its own card, not on the parent.
  const allFailed = entries.length > 0 && entries.every(entryFailed);
  const label = t("agentSteps.subagentCardsLabel", { count: entries.length });

  return (
    <div
      data-testid={`agent-step-subagent-cards-${gridId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-cards-toggle-${gridId}`}
        aria-expanded={expanded}
        aria-label={label}
        onClick={() => setExpanded((value) => !value)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        <StatusGlyph
          done={allDone}
          failed={allFailed}
          testIdSuffix={`cards-${gridId}`}
        />
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {label}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-cards-body-${gridId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          <SubagentCardGrid gridId={gridId} entries={entries} />
        </div>
      ) : null}
    </div>
  );
}
